import { useMutation, useQuery } from './urqlHooks'

const STATEMENT_PERIODS = `
  query STATEMENT_PERIODS($companyId: String!, $year: Int!, $cardId: String) {
    statementPeriods(companyId: $companyId, year: $year, cardId: $cardId) {
      periodKey
      year
      month
      periodDate
      files {
        _id
        companyId
        periodKey
        cardId
        cardLabel
        key
        fileName
        fileType
        uploadedAt
        uploadedByUserId
        uploadedByUserName
        uploadedByUserEmail
        extractionMethod
        totalAmount
        previousPeriodBilledAmountDue
        previousPeriodAmountPaid
        statementAmountCurrency
        previousBalanceBilled
        periodPaymentCredit
        nationalDebtTransfer
        totalBilledAmountDue
        statementDocumentDate
        extractionTotalsOk
        extractionWarning
        extractionReconciliationPath
        extractionReconciliationQuality
        creditCardObligationDocumentId
        creditCardPaso3Completed
        isLocked
        creditCardInModification
        creditCardObligationTypeLabel
        creditCardObligationAmount
        creditCardBankReconciledAt
        creditCardRendicionObligationDocumentId
        creditCardRendicionObligationDocumentIds
        creditCardRendicionObligationTypeLabel
        creditCardRendicionObligationAmount
        history {
          date
          action
          userId
          userName
          userEmail
          cardLabel
          operationIndex
          details
          changes {
            field
            label
            oldValue
            newValue
          }
        }
        extractedDocuments {
          operationId
          originPlace
          date
          description
          originalCurrency
          originalAmount
          installmentLocalAmount
          totalLocalAmount
          installmentClpAmount
          totalClpAmount
          usdToClpExchangeRate
          exchangeRateIndicatorDate
          installmentNumber
          totalInstallments
          section
          associatedDocumentId
          associatedDocumentType
          associatedDocumentLabel
          linkedTcObligationDocumentId
          linkedTcObligationTypeLabel
          suppressPeriodTcObligation
          bankTcReconcileAmountClp
          bankTcReconcileAmountLocal
          obligationType
          obligationTypeLabel
          accountKey
          accountName
          allocations {
            allocationId
            type
            amountLocal
            amountClp
            description
            associatedDocumentId
            associatedDocumentType
            associatedDocumentLabel
            linkedTcObligationDocumentId
            linkedTcObligationTypeLabel
            bankTcReconcileAmountClp
            bankTcReconcileAmountLocal
            accountKey
            accountName
          }
        }
      }
    }
  }
`

const GET_STATEMENTS_UPLOAD_URL = `
  query GET_STATEMENTS_UPLOAD_URL($companyId: String!, $fileName: String!, $fileType: String!) {
    getStatementsUploadUrl(companyId: $companyId, fileName: $fileName, fileType: $fileType) {
      uploadUrl
      fileUrl
      key
      fileId
    }
  }
`

const STATEMENT_DOWNLOAD_URL = `
  query STATEMENT_DOWNLOAD_URL($companyId: String!, $statementId: String!) {
    statementDownloadUrl(companyId: $companyId, statementId: $statementId)
  }
`

const UPLOAD_STATEMENT_FILE = `
  mutation UPLOAD_STATEMENT_FILE($input: UploadStatementFileInput!) {
    uploadStatementFile(input: $input) {
      _id
      periodKey
      cardId
      cardLabel
      key
      fileName
    }
  }
`

const DELETE_STATEMENT_FILE = `
  mutation DELETE_STATEMENT_FILE($input: DeleteStatementFileInput!) {
    deleteStatementFile(input: $input)
  }
`

const SET_STATEMENT_LOCKED = `
  mutation SET_STATEMENT_LOCKED($companyId: String!, $statementId: String!, $locked: Boolean!) {
    setStatementLocked(companyId: $companyId, statementId: $statementId, locked: $locked) {
      _id
      isLocked
    }
  }
`

const ASSOCIATE_STATEMENT_DOCUMENTS = `
  mutation ASSOCIATE_STATEMENT_DOCUMENTS($input: AssociateStatementDocumentsInput!) {
    associateStatementDocuments(input: $input) {
      statementId
      totalOperations
      associatedCount
      unmatchedCount
    }
  }
`

const STATEMENT_ASSOCIATION_DOCUMENT_CANDIDATES = `
  query STATEMENT_ASSOCIATION_DOCUMENT_CANDIDATES($input: StatementAssociationDocumentCandidatesInput!) {
    statementAssociationDocumentCandidates(input: $input) {
      total
      docs {
        id
        documentType
        label
        amount
        invoiceRemaining
        hasOpenTcCupo
        documentDate
        issuerId
        issuerName
        documentId
        summary
      }
    }
  }
`

const ASSIGN_STATEMENT_OPERATION_DOCUMENT = `
  mutation ASSIGN_STATEMENT_OPERATION_DOCUMENT($input: AssignStatementOperationDocumentInput!) {
    assignStatementOperationDocument(input: $input) {
      statementId
      operationIndex
      associatedDocumentId
      associatedDocumentType
      associatedDocumentLabel
      linkedTcObligationDocumentId
      linkedTcObligationTypeLabel
      splitCreated
      invoiceAmount
      differenceAmount
      allocations {
        allocationId
        type
        amountLocal
        amountClp
        description
        associatedDocumentId
        associatedDocumentType
        associatedDocumentLabel
        linkedTcObligationDocumentId
        linkedTcObligationTypeLabel
        bankTcReconcileAmountClp
        bankTcReconcileAmountLocal
        accountKey
        accountName
      }
    }
  }
`

const CLEAR_STATEMENT_OPERATION_ASSIGNMENT = `
  mutation CLEAR_STATEMENT_OPERATION_ASSIGNMENT($input: ClearStatementOperationAssignmentInput!) {
    clearStatementOperationAssignment(input: $input) {
      statementId
      operationIndex
      associatedDocumentId
      associatedDocumentType
      associatedDocumentLabel
    }
  }
`

const CREATE_STATEMENT_CREDIT_CARD_OBLIGATION = `
  mutation CREATE_STATEMENT_CREDIT_CARD_OBLIGATION($input: CreateStatementCreditCardObligationInput!) {
    createStatementCreditCardObligation(input: $input) {
      statementId
      documentId
      amount
      typeLabel
      created
      rendicionDocumentId
      rendicionDocumentIds
      rendicionTypeLabel
      entryId
      entryIds
      entryNumber
      entryNumbers
      emissionEntryCreated
    }
  }
`

export const GetStatementPeriods = (companyId, year, options = {}) => {
  const extraPause = options.pause === true
  const [result, reexecuteQuery] = useQuery({
    query: STATEMENT_PERIODS,
    variables: { companyId, year, cardId: options.cardId || null },
    pause: !companyId || year == null || extraPause,
    requestPolicy: 'cache-and-network',
  })

  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    reexecuteQuery: () => reexecuteQuery({ requestPolicy: 'network-only' }),
  }
}

export const UploadStatementFile = () => {
  const [result, executeMutation] = useMutation(UPLOAD_STATEMENT_FILE)
  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    executeMutation: (variables) => executeMutation(variables),
  }
}

const REEXTRACT_STATEMENT_WITH_UPGRADE_MODEL = `
  mutation REEXTRACT_STATEMENT_WITH_UPGRADE_MODEL($companyId: String!, $statementId: String!) {
    reextractStatementWithUpgradeModel(companyId: $companyId, statementId: $statementId) {
      _id
      extractionTotalsOk
      extractionWarning
    }
  }
`

export const ReextractStatementWithUpgradeModel = () => {
  const [result, executeMutation] = useMutation(REEXTRACT_STATEMENT_WITH_UPGRADE_MODEL)
  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    executeMutation: (variables) => executeMutation(variables),
  }
}

export const DeleteStatementFile = () => {
  const [result, executeMutation] = useMutation(DELETE_STATEMENT_FILE)
  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    executeMutation: (variables) => executeMutation(variables),
  }
}

export const SetStatementLocked = () => {
  const [result, executeMutation] = useMutation(SET_STATEMENT_LOCKED)
  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    executeMutation: (variables) => executeMutation(variables),
  }
}

const UNLOCK_STATEMENTS_BLOCKING_UNMATCH = `
  mutation UNLOCK_STATEMENTS_BLOCKING_UNMATCH($companyId: String!, $statementIds: [String!]!) {
    unlockStatementsBlockingUnmatch(companyId: $companyId, statementIds: $statementIds) {
      unlockedIds
    }
  }
`

export const UnlockStatementsBlockingUnmatch = () => {
  const [result, executeMutation] = useMutation(UNLOCK_STATEMENTS_BLOCKING_UNMATCH)
  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    executeMutation: (variables) => executeMutation(variables),
  }
}

export const AssociateStatementDocuments = () => {
  const [result, executeMutation] = useMutation(ASSOCIATE_STATEMENT_DOCUMENTS)
  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    executeMutation: (variables) => executeMutation(variables),
  }
}

export const GetStatementAssociationDocumentCandidates = (input, pause = false) => {
  const [result, reexecuteQuery] = useQuery({
    query: STATEMENT_ASSOCIATION_DOCUMENT_CANDIDATES,
    variables: { input },
    pause: pause || !input?.companyId || !input?.operationDate,
    requestPolicy: 'network-only',
  })
  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    reexecuteQuery,
  }
}

export const AssignStatementOperationDocument = () => {
  const [result, executeMutation] = useMutation(ASSIGN_STATEMENT_OPERATION_DOCUMENT)
  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    executeMutation: (variables) => executeMutation(variables),
  }
}

export const ClearStatementOperationAssignment = () => {
  const [result, executeMutation] = useMutation(CLEAR_STATEMENT_OPERATION_ASSIGNMENT)
  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    executeMutation: (variables) => executeMutation(variables),
  }
}

export const CreateStatementCreditCardObligation = () => {
  const [result, executeMutation] = useMutation(CREATE_STATEMENT_CREDIT_CARD_OBLIGATION)
  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    executeMutation: (variables) => executeMutation(variables),
  }
}

const SET_STATEMENT_OPERATION_ACCOUNT_KEY = `
  mutation SET_STATEMENT_OPERATION_ACCOUNT_KEY($input: SetStatementOperationAccountKeyInput!) {
    setStatementOperationAccountKey(input: $input) {
      statementId
      operationIndex
      accountKey
      accountName
      allocationId
    }
  }
`

export const SetStatementOperationAccountKey = () => {
  const [result, executeMutation] = useMutation(SET_STATEMENT_OPERATION_ACCOUNT_KEY)
  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    executeMutation: (variables) => executeMutation(variables),
  }
}

const BULK_SET_STATEMENT_OPERATION_ACCOUNT_KEY = `
  mutation BULK_SET_STATEMENT_OPERATION_ACCOUNT_KEY(
    $input: BulkSetStatementOperationAccountKeyInput!
  ) {
    bulkSetStatementOperationAccountKey(input: $input) {
      statementId
      operationIndexes
      accountKey
      accountName
      assignedCount
    }
  }
`

const BULK_ASSIGN_STATEMENT_OPERATION_DOCUMENT = `
  mutation BULK_ASSIGN_STATEMENT_OPERATION_DOCUMENT(
    $input: BulkAssignStatementOperationDocumentInput!
  ) {
    bulkAssignStatementOperationDocument(input: $input) {
      statementId
      operationIndexes
      associatedDocumentId
      associatedDocumentType
      associatedDocumentLabel
      assignedCount
      matchedTotal
    }
  }
`

export const BulkSetStatementOperationAccountKey = () => {
  const [result, executeMutation] = useMutation(BULK_SET_STATEMENT_OPERATION_ACCOUNT_KEY)
  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    executeMutation: (variables) => executeMutation(variables),
  }
}

export const BulkAssignStatementOperationDocument = () => {
  const [result, executeMutation] = useMutation(BULK_ASSIGN_STATEMENT_OPERATION_DOCUMENT)
  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    executeMutation: (variables) => executeMutation(variables),
  }
}

const AUTO_ASSIGN_STATEMENT_ACCOUNT_KEYS = `
  mutation AUTO_ASSIGN_STATEMENT_ACCOUNT_KEYS($input: AutoAssignStatementAccountKeysInput!) {
    autoAssignStatementAccountKeys(input: $input) {
      assigned
      skipped
    }
  }
`

export const AutoAssignStatementAccountKeys = () => {
  const [result, executeMutation] = useMutation(AUTO_ASSIGN_STATEMENT_ACCOUNT_KEYS)
  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    executeMutation: (variables) => executeMutation(variables),
  }
}

const UPDATE_STATEMENT_SUMMARY = `
  mutation UPDATE_STATEMENT_SUMMARY($input: UpdateStatementSummaryInput!) {
    updateStatementSummary(input: $input) {
      statementId
      statementDocumentDate
      previousPeriodBilledAmountDue
      previousPeriodAmountPaid
      previousBalanceBilled
      periodPaymentCredit
      nationalDebtTransfer
      totalBilledAmountDue
      extractionTotalsOk
      extractionWarning
      extractionReconciliationQuality
    }
  }
`

export const UpdateStatementSummary = () => {
  const [result, executeMutation] = useMutation(UPDATE_STATEMENT_SUMMARY)
  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    executeMutation: (variables) => executeMutation(variables),
  }
}

const CREATE_RENDICION_TC_EMISSION_ENTRY = `
  mutation CREATE_RENDICION_TC_EMISSION_ENTRY($input: CreateRendicionTCEmissionEntryInput!) {
    createRendicionTCEmissionEntry(input: $input) {
      entryId
      entryNumber
      created
    }
  }
`

const RENDICION_TC_EMISSION_ENTRY_PREVIEW = `
  query RENDICION_TC_EMISSION_ENTRY_PREVIEW($input: RendicionTCEmissionEntryPreviewInput!) {
    rendicionTCEmissionEntryPreview(input: $input) {
      success
      message
      description
      stringDate
      totalDebit
      totalCredit
      imbalance
      alreadyCreated
      previews {
        success
        message
        description
        stringDate
        totalDebit
        totalCredit
        imbalance
        rows {
          ranking
          accountKey
          accountName
          debit
          credit
        }
      }
      rows {
        ranking
        accountKey
        accountName
        debit
        credit
      }
    }
  }
`

export const CreateRendicionTCEmissionEntry = () => {
  const [result, executeMutation] = useMutation(CREATE_RENDICION_TC_EMISSION_ENTRY)
  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    executeMutation: (variables) => executeMutation(variables),
  }
}

const CREDIT_CARD_STATEMENT_RECONCILE_CANDIDATES = `
  query CREDIT_CARD_STATEMENT_RECONCILE_CANDIDATES(
    $companyId: String!
    $year: Int
    $cardId: String
  ) {
    creditCardStatementReconcileCandidates(
      companyId: $companyId
      year: $year
      cardId: $cardId
    ) {
      statementId
      cardId
      cardLabel
      periodKey
      totalRemaining
      obligations {
        id
        amount
        remaining
        type
        typeLabel
        description
      }
    }
  }
`

const RECONCILE_TRANSACTION_TO_CREDIT_CARD_STATEMENT = `
  mutation RECONCILE_TRANSACTION_TO_CREDIT_CARD_STATEMENT(
    $input: ReconcileTransactionToCreditCardStatementInput!
  ) {
    reconcileTransactionToCreditCardStatement(input: $input) {
      success
      matchedObligationIds
      matchedTotal
      transactionRemainingAfter
    }
  }
`

export const GetCreditCardStatementReconcileCandidates = (variables, pause = false) => {
  const { companyId, year, cardId } = variables || {}
  const [result, reexecuteQuery] = useQuery({
    query: CREDIT_CARD_STATEMENT_RECONCILE_CANDIDATES,
    variables: {
      companyId,
      year: year != null ? year : null,
      cardId: cardId || null,
    },
    requestPolicy: 'network-only',
    pause: pause || !companyId,
  })
  return { ...result, reexecuteQuery }
}

export const ReconcileTransactionToCreditCardStatement = () => {
  const [result, executeMutation] = useMutation(RECONCILE_TRANSACTION_TO_CREDIT_CARD_STATEMENT)
  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    executeMutation: (variables) => executeMutation(variables),
  }
}

export const useRendicionTCEmissionEntryPreview = (variables, pause = false) => {
  const { companyId, statementId } = variables || {}
  const [result, reexecuteQuery] = useQuery({
    query: RENDICION_TC_EMISSION_ENTRY_PREVIEW,
    variables: {
      input: {
        companyId,
        statementId,
      },
    },
    requestPolicy: 'network-only',
    pause: pause || !companyId || !statementId,
  })
  return { ...result, reexecuteQuery }
}

export const STATEMENT_SUMMARY_FOR_UNMATCH = `
  query STATEMENT_SUMMARY_FOR_UNMATCH($companyId: String!, $originId: String!, $endId: String!) {
    statementSummaryForUnmatch(companyId: $companyId, originId: $originId, endId: $endId) {
      statementId
      cardId
      cardLabel
      periodKey
      fileName
      blocked
      blockReason
      closedPeriodLabel
      bankTransactions {
        id
        date
        description
      }
      participants {
        statementId
        cardId
        cardLabel
        periodKey
        fileName
        isLocked
      }
    }
  }
`

export { GET_STATEMENTS_UPLOAD_URL, STATEMENT_DOWNLOAD_URL }

export const STATEMENTS_PARTICIPATING_IN_MODULE_TC = `
  query STATEMENTS_PARTICIPATING_IN_MODULE_TC($companyId: String!, $obligationId: String!) {
    statementsParticipatingInModuleTc(companyId: $companyId, obligationId: $obligationId) {
      statementId
      cardId
      cardLabel
      periodKey
      isLocked
      hasIntactBankSnapshot
      hasOpenBankCupo
    }
  }
`
